import { UserMenu } from '@/components/UserMenu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface AppShellProps {
  children: ReactNode;
  /** Slot rendered inside the topbar between brand and user menu. */
  topbar?: ReactNode;
  /** Centered content max width. Defaults to lg. */
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
}

const WIDTHS = {
  sm: 'max-w-2xl',
  md: 'max-w-4xl',
  lg: 'max-w-6xl',
  xl: 'max-w-7xl',
  full: 'max-w-none',
};

export function AppShell({ children, topbar, width = 'lg', className }: AppShellProps) {
  return (
    <div className="min-h-screen bg-bg text-fg">
      <header className="sticky top-0 z-30 border-b border-line bg-bg/85 backdrop-blur">
        <div
          className={cn('mx-auto flex h-14 items-center gap-3 px-4 sm:h-16 sm:px-6', WIDTHS[width])}
        >
          {/* shrink-0 is load-bearing, not decoration. The span is EMPTY -- its
              width comes from height x aspect-ratio, so its min-content width
              is 0 and it is the first thing flex sacrifices. The topbar beside
              it is flex-1 with no min-w-0, so it yields nothing and the logo
              absorbed all the shrinkage: on the legacy MG dashboard, whose
              topbar carries region buttons, date controls, a theme toggle and
              Update Data, it collapsed to zero width and vanished. Every
              lighter topbar left enough room, which is why it looked fine
              everywhere else. Jay reported it missing there on 2026-09-02. */}
          <Link to="/" className="flex shrink-0 items-center" aria-label="Adholics home">
            <span
              role="img"
              aria-label="Adholics."
              className="brand-logo h-4 shrink-0 sm:h-[18px]"
            />
          </Link>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            {topbar}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="ghost" size="icon" aria-label="Help & docs">
                  <Link to="/support">
                    <HelpCircle className="size-4" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Help &amp; docs</TooltipContent>
            </Tooltip>
            <UserMenu />
          </div>
        </div>
      </header>
      <main className={cn('mx-auto px-4 py-6 sm:px-6 sm:py-10', WIDTHS[width], className)}>
        {children}
      </main>
    </div>
  );
}

interface AuthShellProps {
  children: ReactNode;
  /** Optional eyebrow above the card (eg "Welcome to Adholics"). */
  intro?: ReactNode;
  className?: string;
}

export function AuthShell({ children, intro, className }: AuthShellProps) {
  return (
    <div className="relative flex min-h-screen flex-col bg-bg text-fg">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top,_var(--accent-soft)_0%,_transparent_55%)]"
      />
      <header className="relative z-10 px-6 pt-8">
        <Link to="/" className="inline-flex items-center" aria-label="Home">
          <span role="img" aria-label="Adholics." className="brand-logo h-5 shrink-0" />
        </Link>
      </header>
      <main className="relative z-10 flex flex-1 items-center justify-center px-6 py-12">
        <div className={cn('w-full max-w-md space-y-6', className)}>
          {intro}
          {children}
        </div>
      </main>
    </div>
  );
}
